import { useCallback } from "react";
import { useWorkOrderStore } from "../../store/wo-order";
import useGetWorkOrder from "../useGetWorkOrder";
import {
  getCCAFieldDefinitions,
  getCompleteColumnDetails,
  getExistingCodes,
  getMappingSource,
} from "../../api/AnalysisCodes";
import { WorkOrder } from "../../models/workorders/WorkOrder";
import { ApiResponse } from "../../models/api/ApiResponse";
import { AnalysisCode } from "../../models/analysiscodes/AnalysisCodes";

export default function useGetAnalysisCodes() {
  const { trigger } = useGetWorkOrder();
  const setWoCCAFieldDefinitions = useWorkOrderStore(
    (state) => state.setWoCCAFieldDefinitions
  );
  const setWoCompleteColumnDetails = useWorkOrderStore(
    (state) => state.setWoCompleteColumnDetails
  );
  const setAnalysisCodes = useWorkOrderStore(
    (state) => state.setWoAnalysisCodes
  );
  const setIsNewRelationship = useWorkOrderStore(
    (state) => state.setIsNewRelationship
  );

  const getAnalysisCodes = useCallback(
    async (woNumber: string) => {
      let workOrder = useWorkOrderStore.getState().getWorkOrderById(woNumber);
      let woResponse: ApiResponse | undefined = undefined;
      if (!workOrder) {
        woResponse = await trigger(woNumber);
        if (!woResponse.isError) workOrder = woResponse.data as WorkOrder;
      }
      if (workOrder) {
        const events = workOrder?.Events;

        const fields = await getCCAFieldDefinitions(workOrder?.Equipment);
        setWoCCAFieldDefinitions(woNumber, fields);

        const mappingSource = await getMappingSource();
        const isNewRelationship =
          !!mappingSource && mappingSource === "NewRelationship";

        if (!isNewRelationship && workOrder?.EquipmentClass) {
          const oldRelColumns = await getCompleteColumnDetails(
            workOrder?.Equipment
          );
          setWoCompleteColumnDetails(woNumber, oldRelColumns);
        }

        let existingCodes: AnalysisCode[] = [];
        if (events?.DateCreated && events?.TimeCreated) {
          existingCodes =
            (await getExistingCodes(events.DateCreated, events.TimeCreated)) ??
            [];
        }
        setAnalysisCodes(woNumber, existingCodes ?? []);
        setIsNewRelationship(isNewRelationship);
      }
      return woResponse;
    },
    [
      setAnalysisCodes,
      setIsNewRelationship,
      setWoCCAFieldDefinitions,
      setWoCompleteColumnDetails,
      trigger,
    ]
  );

  return { getAnalysisCodes };
}
